import { z } from "zod";
import { factSourceSchema } from "./athlete";
import { lineSourceSchema, movementUnitSchema, ratingSchema } from "./logs";

/**
 * ONE MOVEMENT, ACROSS EVERY SESSION THAT EVER CONTAINED IT.
 *
 * ═══ WHY THIS IS ITS OWN DOMAIN AND NOT A CORNER OF `logs` ═══
 *
 * A log is one SESSION. Everything the logs domain answers is keyed by a day: history is a
 * list of days, a detail is one day, the benchmarks are the days that were kept. This is the
 * other axis entirely — every back squat the athlete has ever done, in order, with the
 * sessions collapsed away — and it is answered by aggregating ACROSS logs rather than by
 * reading one.
 *
 * The two questions want opposite indexes, opposite page shapes and opposite cache lifetimes:
 * a log is invalidated by the debrief that wrote it, a movement's history is invalidated by
 * any debrief that touched that movement. Folding this into `logs` would give that domain two
 * unrelated read models under one name, which is the definition of a domain that has become
 * two. `framework/architecture` states the rule the other way round — "a domain that outgrows
 * this has become two domains" — and this is the split, made before the outgrowing.
 *
 * ⚠️ IT OWNS NO TABLE. Every number here is an aggregate over `session_log_lines`, which the
 * logs domain writes. That is deliberate and it is why this domain is READ-ONLY: there is no
 * `POST /v1/movements` and there must not be one, because the only way a movement gains
 * history is by an athlete finishing a session.
 *
 * ═══ 🚨 THE HONESTY THAT HAS TO SURVIVE EVERY AGGREGATE ═══
 *
 * `session_log_lines.source` separates a set a person entered (`logged`) from one the service
 * computed out of a metcon's rounds (`derived`). Both are real work and both belong in a
 * volume; only one was measured. Every shape below therefore carries `measuredSets` beside
 * `sets`, so a screen can say how much of a number somebody actually typed — and a screen
 * that draws a total without it is drawing a number that looks better sourced than it is.
 */

/* ══════════════════════════════════════════════════════════════════════════════════════
   THE ATHLETE'S MAX — one definition, printed wherever a max is printed
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * ═══ 🚨 THE ONE NUMBER THAT MAY BE CALLED THE ATHLETE'S MAX, AND WHERE IT COMES FROM (BUD-375) ═══
 *
 * It is the figure the ENGINE prices the next prescription from — `maxFor` in
 * `api/src/engine/capability/strength.ts`, resolved over the athlete's profile and their own
 * logged sets — and nothing else. Before this, the library row and the movement screen's
 * headline carried an estimate this domain computed for itself ("the best set of the most recent
 * day"), while the engine loaded the bar off a different rule ("highest admissible: a newer AND
 * higher estimate beats a recorded max, capped"). An athlete with 140 kg on file and an easy set
 * of five last week saw "Estimated 1RM 110 kg" in the largest type on the screen and was
 * prescribed off 140. Two numbers, one word, thirty kilos apart.
 *
 * So this domain no longer has a definition of its own. It asks the engine, once per request,
 * and every surface that prints a max prints this one. The chart's per-bucket estimate
 * (`movementPointSchema.estimatedMaxKg`) is a different thing — a trend, best-in-bucket — and is
 * never labelled as the athlete's max.
 *
 * FLATTENED, like `sessions.ts` flattens the same fact: a nested optional is a field a client
 * reads as a permission. All three are null together for a movement the engine has no max for —
 * unloaded, a discrete implement, or nothing known about this person at all.
 */
const movementMaxFields = {
  /** The engine's resolved max, in kilos. Null when the engine has no max for this movement. */
  maxKg: z.number().positive().nullable(),
  /**
   * WHERE THE NUMBER CAME FROM — `measured` · `reported` are a max a person put in; `estimated`
   * is one computed from a set they logged; `derived` came through the ratio chain from another
   * lift; `assumed` is the population model. The screen says which, beside the number, because a
   * figure labelled "1RM" is read as a weight somebody has lifted.
   */
  maxSource: factSourceSchema.nullable(),
  /** The day the winning fact was observed, `YYYY-MM-DD`. Null when nothing dated it. */
  maxObservedAt: z.iso.date().nullable(),
  /**
   * ═══ 🚨 IS THIS NUMBER OLD? — DATED, NEVER REDUCED (BUD-390) ═══
   *
   * True when the winning fact carries a day and that day is at least `maxRetestDays` — 84, one
   * twelve-week strength cycle — behind today. `api/src/engine/capability/facts.ts` owns the
   * doctrine and this carries its verdict: "a stale fact is not an unknown fact… keeps its value,
   * loses certainty toward a floor it never reaches, and emits an intent to measure it again".
   * So `maxKg` is UNCHANGED beside this flag. A screen that dimmed, discounted or hid the number
   * on the strength of it would be re-introducing the decay the engine deliberately does not do.
   *
   * 🚨 IT IS MEASURED FROM THE LAST QUALIFYING SET, NOT THE LAST TIME THE LIFT WAS TOUCHED, and
   * that falls out of the engine rather than being arranged here: an `estimated` fact carries the
   * day of the SET it was computed from, and a submaximal set never wins the resolution
   * (`bestKnownMax` is "highest admissible"). So somebody who front squats weekly at 60% and
   * never goes heavy keeps the old day, and goes stale on schedule — which is exactly the athlete
   * the flag is for.
   *
   * ⚠️ FALSE WHEN NOTHING DATED THE FACT. An `assumed` max off the population model has no day,
   * and an undated fact has no age; the honest answer there is to mark nothing rather than to
   * mark it wrong.
   *
   * ⚠️ NOT THE ENGINE'S `RetestIntent`. That answers a different question — "should the draw
   * program a build to a heavy single" — and it is gated by `isTestable`, which is twelve lifts.
   * A bent-over row's max can be two years old and is still not a lift anybody tests.
   */
  maxStale: z.boolean(),
  /**
   * THE SET THE NUMBER WAS COMPUTED FROM — `147.5 × 3`, so a row can say where it came from.
   *
   * ⚠️ PRESENT ONLY WHEN `maxSource` IS `estimated`, because that is the only source with a set
   * behind it. A `measured` or `reported` max is a number a person put in — `maxObservedAt` is
   * the day they put it in, and there is no set to name. `derived` came through the ratio chain
   * from a DIFFERENT lift, and printing that lift's set under this one's name would be a fact
   * about the wrong movement.
   */
  maxFromSet: z
    .object({
      loadKg: z.number().positive(),
      reps: z.number().int().positive(),
    })
    .nullable(),
  /**
   * ⚠️ DEPRECATED — the same number as `maxKg`, under the name an installed bundle still reads.
   * It used to be this domain's own estimate; it is now the engine's resolved max, so an older
   * screen printing it under "Estimated 1RM" prints the right number under a stale word. Nothing
   * new reads it. Removing it from the wire needs a version floor, not a diff.
   */
  estimatedMaxKg: z.number().min(0).nullable(),
} as const;

/* ══════════════════════════════════════════════════════════════════════════════════════
   THE LIBRARY — everything this athlete has ever performed
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * ONE ROW OF THE MOVEMENT LIST — enough to draw a row and to decide what its chart offers.
 *
 * `loaded` IS THE FIELD THAT CHANGES THE SCREEN. A back squat has a weight and its natural
 * chart is load over time; a pull-up has none, and a "top weight" chart of it is a flat line
 * at zero under a heading that implies the athlete is not progressing. So the movement itself
 * says whether a load has ever been on it, and the screen offers the metrics that exist rather
 * than the metrics the layout has room for.
 */
export const movementSummarySchema = z.object({
  /** The catalog slug — `back-squat`. The identity, and the path segment. */
  movement: z.string().min(1).max(80),
  /**
   * The name as it was last printed on a card. Carried on every line rather than looked up:
   * the engine publishes no catalog the database can read. See `sessionLogLineSchema`.
   */
  name: z.string().min(1).max(120),
  unit: movementUnitSchema,
  /** True once any set of it has ever carried a load above zero. Decides whether volume means anything. */
  loaded: z.boolean(),
  /**
   * HAS A PERSON EVER RECORDED A WEIGHT FOR IT — a different question from `loaded`, and
   * conflating the two put "Best set 42.5 kg × 126" on a thruster screen.
   *
   * 🚨 `loaded` ASKS WHETHER A VOLUME IS MEANINGFUL: yes if any set carried weight, derived
   * metcon lines included, because metcon work is real work and belongs in a training volume.
   *
   * THIS asks whether a TOP WEIGHT or a BEST SET is meaningful, and only a set a person entered
   * can answer it. A derived line carries the load the workout PRESCRIBED and a whole block's
   * rep total; a movement that only ever appears in metcons therefore has neither, and a chart
   * of its prescribed load is a flat line that reads as an athlete who has not progressed.
   */
  measuredLoad: z.boolean(),
  /**
   * `weightlifting` · `monostructural` · `gymnastic` — what the library GROUPS on.
   *
   * A plain string, matching how it travels everywhere else: the registry is rows in code and a
   * fourth one must not be a wire break. Null for a movement whose every recorded set predates
   * the field; the screen files those under "Other" rather than guessing.
   */
  modality: z.string().min(1).max(40).nullable(),
  /** The day it was last performed, `YYYY-MM-DD` in the athlete's own calendar. */
  lastPerformedOn: z.iso.date(),
  /** Sessions that contained it — not sets. Two sets in one session are one session. */
  sessions: z.number().int().min(0),
  sets: z.number().int().min(0),
  /** How many of `sets` a person actually entered. See the honesty note at the top. */
  measuredSets: z.number().int().min(0),
  /** Total count in `unit` — reps, or calories, or metres. Never a sum across units. */
  reps: z.number().int().min(0),
  /** Σ(reps × kilos) over the sets that carried a load. Zero for an unloaded movement. */
  volumeKg: z.number().min(0),
  /**
   * ═══ 🚨 IS THE WEIGHT ON A BAR — THE CATALOG'S ANSWER, NEVER A LIST OF NAMES (BUD-390) ═══
   *
   * `isBarbellLoaded` in `api/src/engine/capability/strength.ts`: the lowest `loadBearing` rank
   * among the movement's own `eq` is the barbell. So a bench press is barbell (the bar carries
   * the weight, not the bench) and a KB swing is not, and neither fact is written down twice.
   *
   * 🔑 IT TRAVELS RATHER THAN BEING DERIVED ON THE PHONE BECAUSE THE CATALOG IS NOT THERE. The
   * engine publishes no catalog the app can read — `movement_name` is carried on every logged
   * line for exactly that reason — so the alternative is nineteen slugs hard-coded in a screen,
   * which is the thing `movements.ts` in the engine opens by refusing: "NOTHING IN THE ENGINE
   * TARGETS A MOVEMENT BY NAME".
   *
   * ⚠️ FALSE FOR A SLUG THE CATALOG DOES NOT KNOW, which is the honest answer: a movement whose
   * every recorded set predates the catalog row cannot be claimed as a barbell lift.
   */
  barbell: z.boolean(),
  /**
   * ═══ 🚨 THE LINE BEHIND THE NUMBER — frame `8a` (BUD-398) ═══
   *
   * Monthly estimates over the last twelve months, oldest first, so a row can draw where this
   * lift has BEEN rather than only where it is. A single figure answers "what can I lift"; it
   * cannot answer "is this going anywhere", which is the question an athlete scanning a list of
   * their own lifts is actually asking.
   *
   * ⚠️ NULLS SURVIVE AND ARE NOT FILTERED OUT. A month the lift was trained but nothing carried
   * a measurable weight has no estimate, and dropping the point would slide every later one a
   * step left — drawing a smooth run of progress across a month containing no such number. The
   * line breaks there instead, exactly as the full-size chart does.
   *
   * 🔑 EVERY MOVEMENT SOMEBODY HAS RECORDED A WEIGHT FOR, AND EMPTY FOR THE REST. Keyed on
   * `measuredLoad`, never on `barbell`: a dumbbell press, a kettlebell clean and a bar are all
   * loads an athlete chose and can get stronger at. Settled with the product owner 2026-09-21 —
   * *"i want the weight also for dumbells, etc. or what ever we trained"*.
   *
   * ⚠️ AND `measuredLoad` RATHER THAN `loaded`, the field directly above. `loaded` counts a
   * derived metcon line, so it is true for a wall ball nobody has ever logged a set of — and a
   * line drawn from a prescribed load is flat, which reads as an athlete who has not progressed.
   *
   * Empty is "no weight was ever recorded for this", never "trained and flat".
   */
  series: z
    .array(
      z.object({
        /** First day of the month, `YYYY-MM-DD`. */
        periodStart: z.iso.date(),
        /** The month's best estimate, or null where nothing in it could produce one. */
        estimatedMaxKg: z.number().nullable(),
      }),
    )
    .max(13),
  /**
   * HOW FAR THIS LIFT HAS MOVED SINCE NEW YEAR, in kilos — the `+21 kg this year` of frame `8a`.
   *
   * 🚨 BOTH ENDS COME FROM THE SAME MEASUREMENT AND NEITHER IS THE STORED MAX. The headline
   * figure may be `reported` or `measured` — a number somebody typed, or a bar they actually
   * tested — while the baseline could only ever be an estimate off a logged set. Subtracting one
   * from the other prints a year of progress that is really the gap between two different kinds
   * of claim, and it would move the day an athlete typed a number in. So this is
   * `best estimate this calendar year − best estimate before it`, and it is null when either
   * end is missing rather than being reported as a gain from zero.
   *
   * ⚠️ IT CAN BE NEGATIVE. A year in which the best set was lighter than last year's is a real
   * answer and the screen says so; clamping it at zero would make the figure decorative.
   */
  yearChangeKg: z.number().nullable(),
  /**
   * THE ONE NUMBER A LIST ROW PRINTS — the engine's max, see `movementMaxFields`. It is the
   * same resolver, over the same profile, as the movement screen's headline: a row saying
   * 140 kg cannot open a screen saying 110.
   */
  ...movementMaxFields,
});

export type MovementSummaryDTO = z.infer<typeof movementSummarySchema>;

/**
 * THE WHOLE LIBRARY IN ONE ANSWER, AND DELIBERATELY NOT PAGINATED.
 *
 * ⚠️ THIS IS THE ONE READ IN THE PROJECT THAT IS NOT KEYSET-PAGINATED, so it needs a reason
 * rather than an oversight. The set is BOUNDED BY THE CATALOG, not by how long somebody has
 * trained: an athlete of ten years has performed a few hundred distinct movements and will
 * never have more, because the catalog is a few hundred movements long. A row is small.
 *
 * And the screen is a SEARCH: the athlete types "squ" and expects the list to narrow as they
 * type. A round trip per keystroke on a gym's wifi is the difference between a search box and
 * a search box that feels broken, so the whole set is fetched once and filtered on the phone.
 * `MOVEMENT_LIBRARY_MAX` is the bound that keeps one request from becoming the outage.
 */
export const MOVEMENT_LIBRARY_MAX = 500;

export const movementListSchema = z.object({
  items: z.array(movementSummarySchema).max(MOVEMENT_LIBRARY_MAX),
  /**
   * TRUE WHEN THE CEILING WAS REACHED, so a screen can say the list is not everything rather
   * than quietly showing a subset. An athlete who hits this has performed 500 distinct
   * movements, which is more than the catalog holds — so in practice it says a bug, not a
   * prolific athlete, and it should be visible rather than silent.
   */
  truncated: z.boolean(),
});

export type MovementListDTO = z.infer<typeof movementListSchema>;

/* ══════════════════════════════════════════════════════════════════════════════════════
   ONE MOVEMENT'S HISTORY — the window, the bucket, and the series
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * HOW FAR BACK. Four windows, because a chart with every point since the beginning of time is
 * unreadable on a phone and a chart of the last three months cannot show a year of progress.
 */
export const MOVEMENT_RANGES = ["3m", "1y", "3y", "all"] as const;
export const movementRangeSchema = z.enum(MOVEMENT_RANGES);
export type MovementRange = (typeof MOVEMENT_RANGES)[number];

/**
 * HOW COARSE. One point per day, per week or per month.
 *
 * ⚠️ THE BUCKET IS NOT COSMETIC AND IT CHANGES WHAT A POINT MEANS. Daily, a point is one
 * session and "volume" is that session's volume. Weekly, a point is however many sessions fell
 * in that week and the volume is their sum — which is the number a training week is actually
 * judged on, and why weekly is the default. A screen that switches bucket without relabelling
 * is showing a different quantity under the same axis.
 *
 * WEEKS START ON MONDAY, and Sunday belongs to the week before. Every other week boundary in
 * this product agrees (`src/features/logs/logs.trend.ts`), and two different weeks in one app
 * is a bug nobody can see and everybody feels.
 */
export const MOVEMENT_BUCKETS = ["day", "week", "month"] as const;
export const movementBucketSchema = z.enum(MOVEMENT_BUCKETS);
export type MovementBucket = (typeof MOVEMENT_BUCKETS)[number];

/**
 * ONE POINT ON THE CHART — every metric, so the screen switches between them without a refetch.
 *
 * 🔑 THAT IS THE WHOLE DESIGN OF THIS SHAPE. The metric is a chip at the top of a chart, and a
 * chip that costs a round trip on a gym's wifi is a chip nobody presses twice. All five
 * quantities over one window are a handful of numbers per point; fetching them together and
 * switching locally is what makes the control feel like a control.
 *
 * `periodStart` is the first day of the bucket, `YYYY-MM-DD`, so the tooltip can say "week of
 * 27 Apr" without the phone having to reconstruct which week a timestamp fell in.
 */
export const movementPointSchema = z.object({
  periodStart: z.iso.date(),
  /** Distinct sessions inside this bucket. A tooltip's "1 session" / "3 sessions". */
  sessions: z.number().int().min(0),
  sets: z.number().int().min(0),
  /** How many of `sets` a person entered rather than the service deriving. */
  measuredSets: z.number().int().min(0),
  /** Total count in the movement's own unit. */
  reps: z.number().int().min(0),
  /** Σ(reps × kilos). Zero where nothing in this bucket carried a load. */
  volumeKg: z.number().min(0),
  /** The heaviest single set in the bucket. Null where nothing carried a load. */
  topLoadKg: z.number().min(0).nullable(),
  /** The biggest single set, in `unit`. Null where nothing was counted. */
  bestReps: z.number().int().min(0).nullable(),
  /**
   * THE REPS OF THE HEAVIEST SET — and emphatically not `bestReps`.
   *
   * 🚨 A bucket holding 140 kg × 3 and 100 kg × 10 has a top weight of 140 and a maximum rep
   * count of 10, and those describe TWO DIFFERENT SETS. Printed as a pair they describe a set
   * nobody did. This is the reps off the heaviest set itself, so `(topLoadKg, topSetReps)` is
   * always one real set — which is what lets a chart plot weight against reps honestly, and what
   * makes an estimate explicable ("147.5 × 3 → 159.3").
   */
  topSetReps: z.number().int().min(0).nullable(),
  /**
   * 🚨 THE ESTIMATED ONE-REP MAX — the best one this bucket produced, in kilos.
   *
   * THE NUMBER THAT ACTUALLY SHOWS PROGRESS. A top weight cannot: 100 kg × 5 and 110 kg × 1 are
   * the same athlete on a good week, and a top-weight chart calls the second one an improvement.
   * This folds the reps and the weight into one comparable figure, so a five-rep day and a heavy
   * single land on the same axis.
   *
   * ⚠️ IT IS AN ESTIMATE AND EVERY SURFACE MUST SAY SO. `weight × factor(reps)`, from the
   * product owner's own coefficient table — 1.00 at a single, 1.15 at five, 1.33 at ten, 2.10 at
   * thirty — computed only from sets a person actually recorded. A set above thirty reps
   * produces nothing rather than an extrapolation past the table's end.
   *
   * 🚨 AND IT IS COMPUTED FROM THIS MOVEMENT'S OWN SETS ALONE. The engine can infer an
   * unrecorded max for one lift from OTHER lifts' recorded maxes through a chain of ratios;
   * that is right for pricing a session and wrong for a progress chart, where the line would
   * move because a different lift moved. Ten deadlifts at 110 kg is the whole input — no
   * recorded max needed, no other movement consulted.
   *
   * NULL where no set in the bucket could produce one: nothing measured, nothing loaded, or
   * every set above the rep ceiling. Not zero — a zero would draw a collapse that never
   * happened, and the chart breaks its line instead.
   *
   * ⚠️ IT IS NOT `oneRepMax`. That one is RECORDED, a number the athlete or the app put in.
   * This one is derived from what they lifted. Both are useful and they must never be printed
   * under the same word.
   */
  estimatedMaxKg: z.number().min(0).nullable(),
});

export type MovementPointDTO = z.infer<typeof movementPointSchema>;

/**
 * THE BEST SET, AND WHICH SESSION IT HAPPENED IN.
 *
 * "BEST" IS HEAVIEST FIRST AND BIGGEST SECOND, which is the right order for a loaded movement
 * and degrades correctly for an unloaded one: with no load anywhere, every candidate ties on
 * the first key and the biggest set wins, which is the only answer a pull-up has.
 *
 * ⚠️ THREE DIFFERENT NUMBERS LIVE ON THIS SCREEN AND THEY MUST NEVER BE CONFLATED:
 *
 *   · BEST SET      — the heaviest set actually performed, reported as it was done (95 kg × 21).
 *   · RECORDED 1RM  — a max the athlete or the app entered, with its provenance and date.
 *   · ESTIMATED 1RM — derived from recorded sets through the coefficient table, and the one the
 *                     progress chart plots.
 *
 * This is the FIRST of the three. It is not converted into either of the others: 95 kg × 21 is
 * printed as 95 kg × 21, because the athlete did that and it is a fact rather than a derivation.
 *
 * `logId` is carried so the screen can open the session it came from — a best set with no way
 * back to the day it happened is trivia.
 */
export const movementBestSetSchema = z.object({
  loadKg: z.number().min(0).nullable(),
  reps: z.number().int().min(0).nullable(),
  performedOn: z.iso.date(),
  logId: z.uuid(),
});

export type MovementBestSetDTO = z.infer<typeof movementBestSetSchema>;

/**
 * THE ATHLETE'S RECORDED ONE-REP MAX FOR THIS MOVEMENT.
 *
 * 🚨 RECORDED, NEVER ESTIMATED. It comes from `athlete_limits` — a number the athlete or the
 * app actually put in — and it carries its own provenance, because a max without one is not a
 * max: `measured` is a lift that happened, `reported` is one they told us about, and the engine
 * ages both from `observedAt` rather than expiring them.
 *
 * Null means nobody has ever recorded one, and a screen SAYS that rather than quietly showing
 * an estimate in its place.
 *
 * ⚠️ IT IS NOT THE HEADLINE AND IT IS NEVER LABELLED AS THE ATHLETE'S MAX (BUD-375). The
 * headline is `movementMaxFields` — the engine's resolution, which may BE this row when it wins
 * and is an estimate when a newer, higher set beat it. This row marks the strength chart as an
 * EVENT ("tested 140 kg"), the ceiling a plotted set can be read against; printing it a second
 * time under a word meaning "max" would put two different numbers on one screen under one word,
 * which is the bug the headline was rebuilt to remove.
 */
export const movementOneRepMaxSchema = z.object({
  kilos: z.number().positive(),
  /** `measured` · `reported` — the two a person produces. Derived facts are never stored. */
  source: z.string().min(1).max(20),
  /** The day it was observed, `YYYY-MM-DD`. Null on a row recorded without one. */
  observedOn: z.iso.date().nullable(),
});

export type MovementOneRepMaxDTO = z.infer<typeof movementOneRepMaxSchema>;

/**
 * ALL-TIME TOTALS — the four numbers a movement's headline shows.
 *
 * ⚠️ ALL-TIME MEANS ALL TIME, NOT THE SELECTED WINDOW, and that is why it is a separate object
 * rather than a sum of the points. A screen showing "Sessions 142" above a chart of the last
 * three months would otherwise print the three-month count under a heading that says
 * otherwise, and the two would disagree by exactly the amount nobody notices.
 */
export const movementTotalsSchema = z.object({
  sessions: z.number().int().min(0),
  sets: z.number().int().min(0),
  measuredSets: z.number().int().min(0),
  reps: z.number().int().min(0),
  /** Σ(reps × kilos) over everything ever. The "tonnage" figure. */
  volumeKg: z.number().min(0),
});

export type MovementTotalsDTO = z.infer<typeof movementTotalsSchema>;

/** ONE MOVEMENT, WHOLE — the identity, the all-time totals, the best set, and the series. */
export const movementStatsSchema = z.object({
  movement: z.string().min(1).max(80),
  name: z.string().min(1).max(120),
  unit: movementUnitSchema,
  loaded: z.boolean(),
  /**
   * HAS A PERSON EVER RECORDED A WEIGHT FOR IT — a different question from `loaded`, and
   * conflating the two put "Best set 42.5 kg × 126" on a thruster screen.
   *
   * 🚨 `loaded` ASKS WHETHER A VOLUME IS MEANINGFUL: yes if any set carried weight, derived
   * metcon lines included, because metcon work is real work and belongs in a training volume.
   *
   * THIS asks whether a TOP WEIGHT or a BEST SET is meaningful, and only a set a person entered
   * can answer it. A derived line carries the load the workout PRESCRIBED and a whole block's
   * rep total; a movement that only ever appears in metcons therefore has neither, and a chart
   * of its prescribed load is a flat line that reads as an athlete who has not progressed.
   */
  measuredLoad: z.boolean(),
  /** The engine's modality. Carried here too so a deep link into one movement can group it. */
  modality: z.string().min(1).max(40).nullable(),
  /**
   * THE HEADLINE — the engine's max, see `movementMaxFields`. Independent of the window below
   * it: the headline says where the athlete is, the series says what the window did.
   */
  ...movementMaxFields,
  /** Echoed back, so a screen can tell a stale answer from the one it just asked for. */
  range: movementRangeSchema,
  bucket: movementBucketSchema,
  allTime: movementTotalsSchema,
  /** Null when the movement has been performed but nothing about it was ever counted. */
  bestSet: movementBestSetSchema.nullable(),
  /**
   * The athlete's recorded one-rep max, or null if they have never put one in.
   *
   * 🔑 IT IS WHAT MAKES THE TOP-WEIGHT CHART READABLE. A line at 112.5 kg says nothing on its
   * own; the same line under a marked 130 kg max says the athlete has been working at 87% —
   * which is the number a person actually acts on.
   */
  oneRepMax: movementOneRepMaxSchema.nullable(),
  /**
   * Oldest first, one entry per bucket that has something in it.
   *
   * ⚠️ EMPTY BUCKETS ARE OMITTED RATHER THAN SENT AS ZEROS, which is the opposite of what the
   * sessions-per-week chart does, and the difference is what each chart is FOR. That one
   * counts training frequency, where a week off IS the information and a gap must read as a
   * zero. This one plots what a movement did when it was trained; a squat not squatted in
   * March is not a March of zero-kilo squats, and drawing it as one invents a collapse.
   * A reader that wants a continuous axis fills the gaps itself, knowing they are gaps.
   */
  points: z.array(movementPointSchema).max(1200),
});

export type MovementStatsDTO = z.infer<typeof movementStatsSchema>;

/**
 * WHAT `GET /v1/movements/:movement` TAKES — the slug from the path, the window from the query.
 *
 * `domain()` parses `input` from the query AND the path params on a GET, so both live in one
 * schema. The defaults are the view the screen opens on: a year, by week, which is the window
 * a training block is actually judged over.
 */
export const movementStatsQuerySchema = z.object({
  movement: z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9-]+$/, "invalid"),
  range: movementRangeSchema.default("1y"),
  bucket: movementBucketSchema.default("week"),
});

export type MovementStatsQuery = z.infer<typeof movementStatsQuerySchema>;

/* ══════════════════════════════════════════════════════════════════════════════════════
   SETS, BY SESSION — what the athlete actually did, day by day (BUD-387)
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * ONE SET OF THIS MOVEMENT, AS PERFORMED AND AS ASKED FOR.
 *
 * ═══ WHY THIS IS NOT `sessionLogLineSchema` ═══
 *
 * That shape carries the movement's identity on every row — slug, printed name, unit, modality
 * — because a session detail is a list of DIFFERENT movements and each row has to say which one
 * it is. This list is one movement, so all four are constant across every row of every page:
 * repeating them is four strings per set, five sets per session, twenty sessions per page, to
 * restate something the screen already knows from the movement read beside it. `logs.ts` makes
 * the same argument the other way round for its summary — "the summary is not the detail with a
 * field hidden" — and this is the same rule applied to the other axis.
 *
 * ⚠️ `atSeconds` AND `restSeconds` ARE ALSO ABSENT, and deliberately. They are facts about a
 * session's own timeline — where on the clock a set landed, how long was rested before it — and
 * they are read on the session's screen, against the blocks either side of them. Here they would
 * be a number with nothing to be compared to.
 */
export const movementSetSchema = z.object({
  /** Which part of the session it belonged to. A squat in the lift and a squat in a metcon differ. */
  block: z.string().min(1).max(40),
  /**
   * 🚨 `logged` OR `derived`, AND THE SCREEN MUST SHOW WHICH. A derived line is a metcon's
   * prescription multiplied by the rounds banked — one row carrying a whole block's rep total
   * against a weight the workout chose — so it is real work and it is not a set anybody
   * performed. It is listed because it happened; it is marked because valuing it would put an
   * unliftable number on the screen. See `lineSourceSchema` in `logs.ts`.
   */
  source: lineSourceSchema,
  /** 1-based, within this movement in this session. */
  setIndex: z.number().int().positive().max(200),
  /** Completed, in the movement's own unit. Null where nobody counted. */
  reps: z.number().int().min(0).max(100_000).nullable(),
  /** Kilos on the bar. Null for an unloaded movement; zero is an empty bar. */
  loadKg: z.number().min(0).max(1000).nullable(),
  /**
   * THE EFFORT, 1–5, EXACTLY AS TAPPED — 1 *I struggled* … 5 *Super easy*.
   *
   * ⚠️ IT RUNS HARD → EASY AND A NULL IS A NULL. Until BUD-374 the control carried its previous
   * value into the next set, so a block of identical ratings could mean one tap; it does not any
   * more, and an unrated set is genuinely unrated. A reader prints nothing for a null and never
   * substitutes a neighbour's rating. Always null on a `derived` line: nobody was asked.
   */
  feel: ratingSchema.nullable(),
  /**
   * ═══ ⚠️ WHAT THE CARD SAID — AND `null` MEANS NOT KNOWN, NEVER ZERO (BUD-376) ═══
   *
   * A set without its prescription is unreadable after the fact: `100, 100, 80, 70, 70` is a
   * programmed back-off or an athlete retreating, and those mean opposite things. Every line
   * written before BUD-376 carries three nulls, as does every `derived` line — so a reader
   * renders "no target recorded" rather than `0 kg`, and never a completion tick.
   */
  prescribedLoadKg: z.number().min(0).max(1000).nullable(),
  prescribedReps: z.number().int().min(0).max(100_000).nullable(),
  prescribedSets: z.number().int().positive().max(200).nullable(),
});

export type MovementSetDTO = z.infer<typeof movementSetSchema>;

/**
 * ONE SESSION THIS MOVEMENT APPEARED IN — the day, what it cost, and its sets.
 *
 * 🔑 THIS IS THE SHAPE THAT ANSWERS "WHAT DID I DO LAST WEEK", and nothing on this wire could
 * answer it before. `GET /v1/movements/:movement` returns AGGREGATES — points bucketed by
 * period, with period totals — so per-session granularity existed nowhere; the only per-set read
 * was `GET /v1/logs/:id`, one session at a time, reachable only by already knowing its id.
 */
export const movementSessionSchema = z.object({
  /** The session, so the screen can open the day it came from. */
  logId: z.uuid(),
  /** The day it was trained, `YYYY-MM-DD` in the athlete's own calendar. */
  performedOn: z.iso.date(),
  /** What the session was called, as the athlete saw it. */
  title: z.string().min(1).max(120),
  /** Sets of THIS movement in that session — not the session's total. */
  sets: z.number().int().min(0),
  /** How many of them a person entered. `sets - measuredSets` is the derived count. */
  measuredSets: z.number().int().min(0),
  /** Total count in the movement's own unit, across the session. */
  reps: z.number().int().min(0),
  /** Σ(count × kilos) over the sets that carried a load. Zero where nothing did. */
  volumeKg: z.number().min(0),
  /**
   * The heaviest set a PERSON entered that day. Null where they entered none — which is the
   * ordinary answer for a movement that only appeared inside a metcon.
   */
  topLoadKg: z.number().min(0).nullable(),
  /**
   * 🚨 THE BEST ESTIMATE THIS SESSION PRODUCED — the same basis `movementPointSchema` plots,
   * and it is NOT the athlete's max.
   *
   * It is `weight × factor(reps)` over this session's own eligible sets: `logged` only, a real
   * load, and a rep count the coefficient table covers. `derived` lines produce nothing here by
   * construction, which is the load-bearing line of this whole shape — 45 deadlifts at half your
   * max inside a double-under piece would otherwise price a max nobody can lift.
   *
   * ⚠️ THE ATHLETE'S MAX IS `movementMaxFields` ON THE MOVEMENT READ, AND ONLY THAT (BUD-375).
   * This is a TREND figure, per session, exactly as the chart's per-bucket estimate is a trend
   * figure per bucket. A screen prints it as what this day produced, never under a word meaning
   * "your max" — that number is the engine's, it can legitimately be higher than every point on
   * this list, and printing two numbers under one word is the bug BUD-375 existed to remove.
   *
   * Null where no set in the session could produce one. Not zero: a zero would draw a collapse.
   */
  estimatedMaxKg: z.number().min(0).nullable(),
  /**
   * 🚨 DID THIS DAY MOVE THE LINE — true when its estimate beat EVERY session before it.
   *
   * ⚠️ IT IS COMPUTED BY THE SERVICE BECAUSE A PHONE CANNOT COMPUTE IT. The list is paged, so a
   * client holds twenty sessions and has no way of knowing whether the best day on page one
   * beat something on page four. Deriving it on the phone would mark a "new best" that was not
   * one, and the error would grow with the athlete's history — invisible on a test account and
   * wrong for everybody two years in.
   *
   * FALSE IS "WORK", AND WORK IS MOST DAYS. The comparison is against the athlete's own earlier
   * sessions of this movement on the trend basis above; a session with no estimate — everything
   * derived, everything unloaded, everything past the table's last row — is never a new best.
   *
   * ⚠️ TWO-WAY, NOT THREE. Frame `10b` of the design brief asks for `→ 108 · floor 104 · work`,
   * where a day of `Comfortable`/`Super easy` sets a FLOOR rather than a best. That rule reads
   * `feel`, and nothing in this service does: the brief files it under its own "engine asks"
   * (`feel <= 2` qualifies a test, `feel >= 4` sets a floor), and both are unbuilt. Inventing the
   * cut here would make this file a second definition of what counts as a max.
   */
  newBest: z.boolean(),
  /**
   * The sets themselves, in the order they were performed — block, then set index.
   *
   * The ceiling is generous rather than reachable: a strength day is a handful of rows of one
   * movement, and a bound exists so one session cannot become the outage.
   */
  lines: z.array(movementSetSchema).max(200),
});

export type MovementSessionDTO = z.infer<typeof movementSessionSchema>;

/**
 * THE PAGE SIZE, and the ceiling on one.
 *
 * 🚨 IT IS PAGED, AND THAT SETTLES THE BRIEF'S OWN OPEN QUESTION ("every session, or the last
 * five with a More?"). An athlete two years in has hundreds of squat sessions carrying thousands
 * of sets, and "every session" is a response that grows without bound for the people who have
 * used the product most. 20 matches the history list, which is the other list in this product
 * that is read by being scrolled; 100 is the bound that keeps one request from becoming the
 * outage, and it is a ceiling rather than a default so the size cannot be chosen by the caller.
 *
 * KEYSET, NEVER OFFSET — the rule the whole service holds, and it bites here for a specific
 * reason: this list is scrolled while today's session is being written into the top of it.
 */
export const MOVEMENT_SESSIONS_PAGE = 20;
export const MOVEMENT_SESSIONS_PAGE_MAX = 100;

export const movementSessionListSchema = z.object({
  /** Newest first. The order the question is asked in — "what did I do last week". */
  items: z.array(movementSessionSchema).max(MOVEMENT_SESSIONS_PAGE_MAX),
  /** Opaque. Null when this page is the end of the list. */
  nextCursor: z.string().nullable(),
});

export type MovementSessionListDTO = z.infer<typeof movementSessionListSchema>;

/**
 * WHAT `GET /v1/movements/:movement/sessions` TAKES — the slug from the path, the page from the
 * query. `domain()` parses both out of one schema on a GET.
 */
export const movementSessionsQuerySchema = z.object({
  movement: z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9-]+$/, "invalid"),
  cursor: z.string().nullable().optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MOVEMENT_SESSIONS_PAGE_MAX)
    .default(MOVEMENT_SESSIONS_PAGE),
});

export type MovementSessionsQuery = z.infer<typeof movementSessionsQuerySchema>;
